import { InterventionTypeEnum, InterventionTypeOptions } from '@generated';
import React from 'react';
import { InterventionTypeIcon } from '~/libraries/interventions/definition/InterventionDefinitionTypeIcon';

interface GroupByInterventionTypesProps<T extends { interventionTypeId: InterventionTypeEnum; id: string; title: string }> {
	interventions: T[];
	displayIntervention: (intervention: T) => React.ReactNode;
	expandOnLoad?: boolean;
}
export function GroupByInterventionTypes<T extends { interventionTypeId: InterventionTypeEnum; id: string; title: string }>({
	interventions,
	displayIntervention
}: GroupByInterventionTypesProps<T>) {
	const grouped: Record<number, T[]> = [];
	grouped[InterventionTypeEnum.Assess] = interventions
		.filter(x => x.interventionTypeId === InterventionTypeEnum.Assess)
		.toSorted((a, b) => a.title.localeCompare(b.title));
	grouped[InterventionTypeEnum.Instruct] = interventions
		.filter(x => x.interventionTypeId === InterventionTypeEnum.Instruct)
		.toSorted((a, b) => a.title.localeCompare(b.title));
	grouped[InterventionTypeEnum.Perform] = interventions
		.filter(x => x.interventionTypeId === InterventionTypeEnum.Perform)
		.toSorted((a, b) => a.title.localeCompare(b.title));
	return (
		<div className="divide-y divide-gray-100 bg-white">
			{Object.entries(grouped).map(([type, items]: [string, T[]]) => {
				if (items.length === 0) {
					return null;
				}
				const interventionTypeId = Number(type);
				const interventionType = InterventionTypeOptions.find(x => x.id === interventionTypeId);
				if (!interventionType) {
					throw new Error(`Intervention type with ID ${interventionTypeId} not found.`);
				}
				return (
					<div key={type} className="space-y-3 py-4">
						<div className="flex items-center gap-2.5">
							<InterventionTypeIcon interventionTypeId={interventionTypeId} className="size-4 text-gray-400" />
							<span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
								{interventionType.label} ({items.length})
							</span>
						</div>
						<div className="space-y-2 pl-3">
							{items.map(intervention => (
								<React.Fragment key={intervention.id}>{displayIntervention(intervention)}</React.Fragment>
							))}
						</div>
					</div>
				);
			})}
		</div>
	);
}
